import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { HeatmapSettings, ColumnStats } from '../../components/chemistry/MoleculeGrid';
import type { HeatmapColumnConfig } from '../../components/chemistry/HeatmapPanel';

const CHANNEL_NAME = 'heatmap-settings';

const HeatmapPopoutPage: React.FC = () => {
  const [settings, setSettings] = useState<HeatmapSettings | null>(null);
  const [columnStats, setColumnStats] = useState<ColumnStats>({});
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Open channel and listen for init/update messages from parent
  useEffect(() => {
    const ch = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = ch;

    ch.onmessage = (e) => {
      if (e.data?.type === 'init') {
        setSettings(e.data.heatmapSettings);
        setColumnStats(e.data.columnStats);
      }
    };

    // Tell parent we're ready
    ch.postMessage({ type: 'popout-ready' });

    return () => ch.close();
  }, []);

  // Notify parent when this window closes
  useEffect(() => {
    const handleUnload = () => {
      channelRef.current?.postMessage({ type: 'popout-closed' });
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // Send settings changes back to parent
  const handleSettingsChange = useCallback((newSettings: HeatmapSettings) => {
    setSettings(newSettings);
    channelRef.current?.postMessage({ type: 'update', heatmapSettings: newSettings });
  }, []);

  if (!settings) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--hover-bg)', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: '14px', fontWeight: 500 }}>Waiting for heatmap data...</div>
          <div style={{ fontSize: '12px', marginTop: '8px', color: 'var(--text-muted)' }}>Open from the heatmap panel in the editor</div>
        </div>
      </div>
    );
  }

  const numericColumns = Object.entries(columnStats).filter(([_, stat]) => stat.isNumeric);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--card-bg)', fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', background: 'linear-gradient(to right, #10b981, #14b8a6)', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <svg style={{ width: '20px', height: '20px', color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <span style={{ color: 'white', fontWeight: 600, fontSize: '14px' }}>Heatmap Settings</span>
      </div>

      {/* Content */}
      <div style={{ padding: '16px', overflowY: 'auto', maxHeight: 'calc(100vh - 48px)' }}>
        {/* Global Intensity Slider */}
        <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--card-border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: 'var(--text-primary)', marginBottom: '8px' }}>
            <span style={{ fontWeight: 500 }}>Color Intensity</span>
            <span style={{ color: '#059669', fontWeight: 600 }}>{Math.round(settings.intensity * 100)}%</span>
          </div>
          <input
            type="range"
            min="10"
            max="100"
            value={settings.intensity * 100}
            onChange={(e) => handleSettingsChange({
              ...settings,
              intensity: parseInt(e.target.value) / 100
            })}
            style={{ width: '100%', accentColor: '#10b981' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
            <span>Subtle</span>
            <span>Vibrant</span>
          </div>
        </div>

        {/* Per-column settings */}
        {numericColumns.length > 0 ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)' }}>Column Thresholds</span>
              <button
                onClick={() => {
                  const newColumns: { [key: string]: HeatmapColumnConfig } = {};
                  numericColumns.forEach(([colName, stat]) => {
                    newColumns[colName] = { enabled: true, min: stat.min, max: stat.max };
                  });
                  handleSettingsChange({ ...settings, columns: newColumns });
                }}
                style={{ fontSize: '12px', color: '#059669', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Enable All
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {numericColumns.map(([colName, stat]) => {
                const config = settings.columns[colName] || { enabled: true, min: stat.min, max: stat.max };
                const step = (stat.max - stat.min) / 100 || 0.01;
                const precision = stat.max > 100 ? 0 : stat.max > 10 ? 1 : 2;

                return (
                  <div
                    key={colName}
                    style={{
                      padding: '12px',
                      borderRadius: '8px',
                      border: `1px solid ${config.enabled ? '#a7f3d0' : 'var(--card-border)'}`,
                      background: config.enabled ? '#ecfdf5' : 'var(--hover-bg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '8px' }}>
                      <input
                        type="checkbox"
                        checked={config.enabled}
                        onChange={(e) => handleSettingsChange({
                          ...settings,
                          columns: {
                            ...settings.columns,
                            [colName]: { ...config, enabled: e.target.checked }
                          }
                        })}
                      />
                      <span style={{ fontSize: '14px', fontWeight: 500, color: config.enabled ? '#111827' : 'var(--text-secondary)' }}>
                        {colName}
                      </span>
                    </label>

                    {config.enabled && (
                      <div style={{ paddingLeft: '24px' }}>
                        {/* Min slider */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', width: '28px' }}>Min</span>
                          <input
                            type="range"
                            min={stat.min}
                            max={config.max - step}
                            step={step}
                            value={config.min}
                            onChange={(e) => handleSettingsChange({
                              ...settings,
                              columns: {
                                ...settings.columns,
                                [colName]: { ...config, min: parseFloat(e.target.value) }
                              }
                            })}
                            style={{ flex: 1, accentColor: '#10b981' }}
                          />
                          <span style={{ fontSize: '12px', fontFamily: 'monospace', color: 'var(--text-primary)', width: '48px', textAlign: 'right' }}>
                            {config.min.toFixed(precision)}
                          </span>
                        </div>

                        {/* Max slider */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', width: '28px' }}>Max</span>
                          <input
                            type="range"
                            min={config.min + step}
                            max={stat.max}
                            step={step}
                            value={config.max}
                            onChange={(e) => handleSettingsChange({
                              ...settings,
                              columns: {
                                ...settings.columns,
                                [colName]: { ...config, max: parseFloat(e.target.value) }
                              }
                            })}
                            style={{ flex: 1, accentColor: '#10b981' }}
                          />
                          <span style={{ fontSize: '12px', fontFamily: 'monospace', color: 'var(--text-primary)', width: '48px', textAlign: 'right' }}>
                            {config.max.toFixed(precision)}
                          </span>
                        </div>

                        {/* Data range */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)' }}>
                          <span>Data: {stat.min.toFixed(precision)}</span>
                          <span>to {stat.max.toFixed(precision)}</span>
                        </div>

                        <button
                          onClick={() => handleSettingsChange({
                            ...settings,
                            columns: {
                              ...settings.columns,
                              [colName]: { enabled: true, min: stat.min, max: stat.max }
                            }
                          })}
                          style={{ fontSize: '10px', color: '#059669', background: 'none', border: 'none', cursor: 'pointer', marginTop: '4px' }}
                        >
                          Reset to data range
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-secondary)' }}>
            <p style={{ fontSize: '14px' }}>No numeric columns found</p>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Load data to configure heatmap</p>
          </div>
        )}

        {/* Reset All */}
        {numericColumns.length > 0 && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--card-border)' }}>
            <button
              onClick={() => handleSettingsChange({ intensity: 0.4, columns: {} })}
              style={{ width: '100%', padding: '8px', fontSize: '14px', color: 'var(--text-primary)', background: 'var(--hover-bg)', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
            >
              Reset All to Defaults
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeatmapPopoutPage;
